import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export type PromotionPriceInfo = {
  promotionId: string;
  promotionName: string;
  pricingType: string;
  discountPercentage: number | null;
  discountAmount: number | null;
  fixedPrice: number | null;
  calculatedPrice: number;
  effectiveTo: string | null;
};

export type FreeGoodsInfo = {
  buyQuantity: number;
  getQuantity: number;
  getSameProduct: boolean;
  getVariantId: string | null;
  items?: { getVariantId: string; getQuantity: number }[];
  maxRedemptionsPerOrder: number | null;
};

export type BundleItemInfo = {
  requiredVariantId: string;
  requiredQuantity: number;
  itemOrder: number;
};

export type BundleConfigInfo = {
  pricingType: string;
  discountPercentage: number | null;
  discountAmount: number | null;
  fixedPrice: number | null;
  maxBundlesPerOrder: number | null;
  minBundlesPerOrder: number;
  items: BundleItemInfo[];
};

export type ThresholdRuleInfo = {
  thresholdType: string;
  thresholdValue: number;
  discountType: string;
  discountPercentage: number | null;
  discountAmount: number | null;
  applyTo: string;
};

export type ApplicablePromotion = {
  promotionId: string;
  promotionName: string;
  pricingType: string;
  variantId: string;
  freeGoodsRule: FreeGoodsInfo | null;
  bundleConfig: BundleConfigInfo | null;
  thresholdRule: ThresholdRuleInfo | null;
  effectiveTo: string | null;
};

export type PriceBreakdown = {
  variantId: string;
  selectedPriceType: string;
  finalPrice: number;
  b2cPrice: number | null;
  originalBasePrice: number | null;
  basePrice: { groupId: string; vatRate: number; b2bPrice: number; b2cPrice: number | null } | null;
  promotionPrice: PromotionPriceInfo | null;
  applicablePromotions: ApplicablePromotion[];
  /** de-price-service SegmentPriceResult / SpecialPriceResult (buyer-scoped; null without a buyer). */
  segmentPrice?: BuyerPriceInfo | null;
  specialPrice?: (BuyerPriceInfo & { specialPriceName?: string }) | null;
};

export type BuyerPriceInfo = {
  segmentName?: string;
  pricingType: string;
  discountPercent: number | null;
  fixedPrice: number | null;
  calculatedPrice: number;
};

export type BuyerPromotionType = "DISCOUNT" | "FIXED" | "TIER" | "FREE_GOODS" | "BUNDLE" | "THRESHOLD" | "MIXED";

export type BuyerPromotion = {
  id: string;
  name: string;
  type: BuyerPromotionType;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  daysRemaining: number | null;
  summary: string;
  variants: { variantId: string; name: string | null; skuCode: string | null }[];
};

/**
 * B27: active promotions of one supplier that reach the caller (a buyer with an
 * active network to it), soonest-ending first. 403 error.price.access_denied
 * without an active network.
 */
export const buyerPromotions = async (supplierBusinessId: string): Promise<{ rows: BuyerPromotion[] }> => {
  return httpRequest.get("/pricing/buyer/promotions", { supplierBusinessId } as never);
};

export const calculateBatch = async (data: {
  businessId: string;
  buyerBusinessId?: string | null;
  variants: { variantId: string; quantity: number }[];
}): Promise<PriceBreakdown[]> => {
  return httpRequest.post("/ord/price/calculate-batch", data);
};
